import type { Exercise } from '../../types.ts';
export const batch5Reading: Exercise[] = [
  {
    id: 'reading-biosphere',
    category: 'reading',
    title: 'Biosphere 2',
    topic: 'Biosphere 2',
    difficulty: 'Challenging',
    article: {
      title: 'Biosphere 2',
      url: 'https://en.wikipedia.org/wiki/Biosphere_2',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-28',
      preview:
        'Eight people entered a sealed ecological laboratory intended to support human life. The assigned sections follow the engineering and the unexpected interactions between soil, air, plants and the building itself.',
      scope: {
        wordCount: 2203,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Biosphere_2',
            wordCount: 578,
            anchors: [''],
          },
          {
            title: 'Engineering',
            url: 'https://en.wikipedia.org/wiki/Biosphere_2#Engineering',
            wordCount: 262,
            anchors: ['Engineering'],
          },
          {
            title: 'First mission (1991–1993)',
            url: 'https://en.wikipedia.org/wiki/Biosphere_2#First_mission_(1991–1993)',
            wordCount: 588,
            anchors: ['First_mission_(1991–1993)'],
          },
          {
            title: 'Challenges',
            url: 'https://en.wikipedia.org/wiki/Biosphere_2#Challenges',
            wordCount: 775,
            anchors: ['Challenges'],
          },
        ],
      },
    },
    reading: {
      title: 'Biosphere 2 — explanations',
      authorship:
        'Original questions and adapted factual explanations with source attribution; CC BY-SA 4.0.',
      sections: [
        {
          id: 'boundary',
          title: 'Closed to matter is not closed to energy',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Biosphere 2 was designed for very low exchange of material with its surroundings. That does not mean it ran without incoming energy. Sunlight entered through the glazing, and an external energy center supplied electricity and heating or cooling services. A system boundary can restrict one kind of exchange while allowing another; the article’s initial description explicitly separates these roles.',
            },
            {
              kind: 'paragraph',
              text: 'The distinction prevents treating every outside input as the same kind of departure from the experiment. Injecting oxygen changes the material inventory. Supplying energy was part of the design. The project’s experience therefore needs more precise language than either “completely self-sufficient” or “not a closed experiment at all.”',
            },
          ],
        },
        {
          id: 'lungs',
          title: 'Allow volume to change without exchanging the air',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Air expands when heated and contracts when cooled. If a large airtight enclosure forced it to remain at constant volume, daily temperature changes would create pressure differences and substantial forces on the structure. The variable-volume chambers called lungs used movable diaphragms to accommodate the expansion and contraction.',
            },
            {
              kind: 'paragraph',
              text: 'The mechanism concerns physical volume and pressure. It is not the same function as a biological lung exchanging oxygen and carbon dioxide with blood. Calling the structures lungs is a helpful analogy for movement, but does not mean they generated oxygen or replaced the plant and microbial processes controlling gas composition.',
            },
          ],
        },
        {
          id: 'carbon',
          title: 'An unseen sink can hide a source',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Soil microbes respire: they consume oxygen while breaking down organic material and release carbon dioxide. Investigators expected the oxygen decline to have a corresponding carbon-dioxide increase. The missing increase was a clue that another part of the system was taking up carbon dioxide, not proof that respiration had stopped.',
            },
            {
              kind: 'paragraph',
              text: 'The article describes isotopic analysis linking that sink to exposed concrete, where carbon dioxide reacted to form calcium carbonate. Following only the air would miss carbon entering the building material. The explanation requires both the biological source and the chemical sink; either one alone fails to explain the combined observations.',
            },
          ],
        },
        {
          id: 'cycles',
          title: 'Light changes the balance across a day',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Photosynthesis removes carbon dioxide during illuminated periods, while respiration releases it. Respiration does not wait for night, but at night photosynthesis can no longer offset it in the same way. The article reports lower carbon dioxide during daylight and higher levels overnight as the balance between those processes changed.',
            },
            {
              kind: 'paragraph',
              text: 'This is a net-flow explanation. A rising concentration indicates that additions exceed removals, not necessarily that every removal process has vanished. Seasonal light changes and the restricted light entering the glass structure also mattered, so a small enclosed ecosystem did not automatically reproduce Earth’s much larger buffers.',
            },
          ],
        },
        {
          id: 'species',
          title: 'Diversity was a buffer, not a guarantee',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The project deliberately included more species than it expected to persist, a strategy described as species-packing. The hope was that enough organisms would survive to maintain ecological functions even after losses. That differs from predicting that every introduced species would thrive.',
            },
            {
              kind: 'paragraph',
              text: 'The reported outcome included both persistence and disruption: many pollinating insects died, some pests flourished, and the crew actively managed plants and other conditions. A diverse starting list therefore did not remove the need to study interactions or intervene. The question concerns the rationale for redundancy, rather than treating the experiment as either an uncomplicated success or a meaningless failure.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'Biosphere 2 · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/Biosphere_2',
          note: 'Assigned external article; source-derived explanations and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
    },
    questions: [
      {
        id: 'b5-biosphere-1',
        prompt: 'Which statement best describes the system boundary intended for Biosphere 2?',
        options: [
          {
            id: '0',
            text: 'It excluded sunlight as well as material exchange.',
          },
          {
            id: '1',
            text: 'It minimized material exchange while receiving energy from outside.',
          },
          {
            id: '2',
            text: 'It freely exchanged air but excluded electricity.',
          },
          {
            id: '3',
            text: 'It relied only on energy generated by the resident organisms.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Introduction and Engineering describe material closure with solar input and externally supplied energy.',
        sectionIds: ['boundary'],
        evidence:
          'Introduction and Engineering describe material closure with solar input and externally supplied energy. Verified in assigned source section: Introduction.',
        sourceSection: {
          title: 'Wikipedia: Introduction',
          url: 'https://en.wikipedia.org/wiki/Biosphere_2',
        },
      },
      {
        id: 'b5-biosphere-2',
        prompt: 'What problem did the structures called “lungs” address?',
        options: [
          {
            id: '0',
            text: 'Respiration consumed oxygen faster than plants replaced it.',
          },
          {
            id: '1',
            text: 'The ocean’s salinity changed as water evaporated.',
          },
          {
            id: '2',
            text: 'Pollinating insects failed to survive in the habitat.',
          },
          {
            id: '3',
            text: 'Heating and cooling expanded and contracted the enclosed air.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Engineering says variable-volume diaphragms accommodated thermal expansion and contraction without imposing constant-volume forces.',
        sectionIds: ['lungs'],
        evidence:
          'Engineering says variable-volume diaphragms accommodated thermal expansion and contraction without imposing constant-volume forces. Verified in assigned source section: Engineering.',
        sourceSection: {
          title: 'Wikipedia: Engineering',
          url: 'https://en.wikipedia.org/wiki/Biosphere_2#Engineering',
        },
      },
      {
        id: 'b5-biosphere-3',
        prompt:
          'Why did oxygen loss fail to produce the expected corresponding rise in atmospheric carbon dioxide?',
        options: [
          {
            id: '0',
            text: 'Concrete absorbed carbon dioxide into calcium carbonate.',
          },
          {
            id: '1',
            text: 'Microbial respiration ceased when oxygen fell.',
          },
          {
            id: '2',
            text: 'The diaphragms selectively expelled carbon dioxide.',
          },
          {
            id: '3',
            text: 'Plants no longer respired at night.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Challenges identifies concrete carbonation as the sink concealing carbon dioxide released by respiration.',
        sectionIds: ['carbon'],
        evidence:
          'Challenges identifies concrete carbonation as the sink concealing carbon dioxide released by respiration. Verified in assigned source section: Challenges.',
        sourceSection: {
          title: 'Wikipedia: Challenges',
          url: 'https://en.wikipedia.org/wiki/Biosphere_2#Challenges',
        },
      },
      {
        id: 'b5-biosphere-4',
        prompt: 'Why did carbon dioxide generally rise overnight in the account?',
        options: [
          {
            id: '0',
            text: 'The lungs injected carbon dioxide as they contracted.',
          },
          {
            id: '1',
            text: 'Microbes consumed carbon dioxide only when illuminated.',
          },
          {
            id: '2',
            text: 'Respiration dominated without daytime photosynthetic drawdown.',
          },
          {
            id: '3',
            text: 'Nighttime photosynthesis released more carbon dioxide.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Challenges contrasts photosynthetic daytime drawdown with nighttime respiration dominance.',
        sectionIds: ['cycles'],
        evidence:
          'Challenges contrasts photosynthetic daytime drawdown with nighttime respiration dominance. Verified in assigned source section: Challenges.',
        sourceSection: {
          title: 'Wikipedia: Challenges',
          url: 'https://en.wikipedia.org/wiki/Biosphere_2#Challenges',
        },
      },
      {
        id: 'b5-biosphere-5',
        prompt: 'What was the intended purpose of “species-packing”?',
        options: [
          {
            id: '0',
            text: 'Ensure every introduced species would survive unchanged.',
          },
          {
            id: '1',
            text: 'Retain ecological functions even if some introduced species were lost.',
          },
          {
            id: '2',
            text: 'Prevent populations from interacting across food webs.',
          },
          {
            id: '3',
            text: 'Replace plant cultivation with imported supplies.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'First mission describes redundancy of species to maintain food webs and functions despite anticipated losses.',
        sectionIds: ['species'],
        evidence:
          'First mission describes redundancy of species to maintain food webs and functions despite anticipated losses. Verified in assigned source section: First_mission_(1991–1993).',
        sourceSection: {
          title: 'Wikipedia: First mission (1991–1993)',
          url: 'https://en.wikipedia.org/wiki/Biosphere_2#First_mission_(1991–1993)',
        },
      },
    ],
  },
  {
    id: 'reading-openstreetmap',
    category: 'reading',
    title: 'OpenStreetMap',
    topic: 'OpenStreetMap',
    difficulty: 'Challenging',
    article: {
      title: 'OpenStreetMap',
      url: 'https://en.wikipedia.org/wiki/OpenStreetMap',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-28',
      preview:
        'An editable geographic database powers far more than the map on its own website. Read about how places and connections are represented, how mistakes spread and are repaired, and how volunteer mapping supports disaster response.',
      scope: {
        wordCount: 1562,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/OpenStreetMap',
            wordCount: 204,
            anchors: [''],
          },
          {
            title: 'History',
            url: 'https://en.wikipedia.org/wiki/OpenStreetMap#History',
            wordCount: 326,
            anchors: ['History'],
          },
          {
            title: 'Data structure',
            url: 'https://en.wikipedia.org/wiki/OpenStreetMap#Data_structure',
            wordCount: 356,
            anchors: ['Data_structure'],
          },
          {
            title: '"Any tags you like"',
            url: 'https://en.wikipedia.org/wiki/OpenStreetMap#"Any_tags_you_like"',
            wordCount: 90,
            anchors: ['"Any_tags_you_like"'],
          },
          {
            title: 'Quality assurance',
            url: 'https://en.wikipedia.org/wiki/OpenStreetMap#Quality_assurance',
            wordCount: 381,
            anchors: ['Quality_assurance'],
          },
          {
            title: 'Humanitarian aid',
            url: 'https://en.wikipedia.org/wiki/OpenStreetMap#Humanitarian_aid',
            wordCount: 205,
            anchors: ['Humanitarian_aid'],
          },
        ],
      },
    },
    reading: {
      title: 'OpenStreetMap — explanations',
      authorship:
        'Original questions and adapted factual explanations with source attribution; CC BY-SA 4.0.',
      sections: [
        {
          id: 'database',
          title: 'The database is distinct from a rendered map',
          blocks: [
            {
              kind: 'paragraph',
              text: 'OpenStreetMap stores structured geographic information that different applications can render, search, analyze or use for routing. The website’s visible map is one use of that database. Treating the project as a single picture misses why the same contributions can support navigation, printed relief maps and other services.',
            },
            {
              kind: 'paragraph',
              text: 'This separation also explains why downstream applications may show different versions or styles. They can obtain copies of the data and update or display them on their own schedules. The assigned article describes both the geographic database and the ecosystem that uses it; neither should be mistaken for a guarantee that every map service has identical live data.',
            },
          ],
        },
        {
          id: 'topology',
          title: 'Represent a connection, not only a crossing',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A node is a geographic point, and a way is an ordered list of nodes describing a line or area. When two ways share a node, the data can explicitly represent their connection, such as a street intersection. Merely drawing two lines across one another does not convey the same topological relationship.',
            },
            {
              kind: 'paragraph',
              text: 'A relation groups nodes, ways or other relations to represent a more complex structure or relationship. A route spanning several ways or a restriction on a turn needs information beyond one point or one line. These primitives separate geometric position from connections and higher-level meaning, which matters when data are used for routing rather than simply viewed.',
            },
          ],
        },
        {
          id: 'tags',
          title: 'Flexible vocabulary has both benefits and costs',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Tags are key–value pairs describing a feature. The community maintains recommended meanings and a proposal process, but editors can use tags without obtaining approval through that process. This allows contributors to describe new kinds of features rather than waiting for a fixed universal schema to change.',
            },
            {
              kind: 'paragraph',
              text: 'The freedom does not mean that every invented tag will be understood consistently by every consuming program. Shared conventions still make data easier to interpret. The article describes a flexible folksonomy, not a central list that forbids all unapproved tags or a system with no metadata at all.',
            },
          ],
        },
        {
          id: 'review',
          title: 'Publication and correction occur at different stages',
          blocks: [
            {
              kind: 'paragraph',
              text: 'According to the assigned quality-assurance section, edits enter the database without mandatory advance peer review. Basic format validation is narrower than checking whether a road is accurately represented or a tag follows community practice. Volunteers and tools inspect history and map data to identify and correct problems afterward.',
            },
            {
              kind: 'paragraph',
              text: 'An error can persist in a downstream application after it has been corrected in the main database if that application uses a lagging copy. The article gives an example of such replication lag. Fast correction at the source and delayed disappearance elsewhere can therefore both be true; they describe different points in the distribution chain.',
            },
          ],
        },
        {
          id: 'relief',
          title: 'Remote contribution can become field infrastructure',
          blocks: [
            {
              kind: 'paragraph',
              text: 'After the Haiti earthquake, volunteers used available satellite imagery to map roads, buildings and camps. Aid organizations could then use the resulting geographic data in printed maps and navigation devices. The chain runs from imagery interpretation to a shared database to tools used by people working in the field.',
            },
            {
              kind: 'paragraph',
              text: 'The useful output was not limited to a live website that every worker had to keep open. Downloadable and printed maps made the data available through other formats. This illustrates the benefit of reusable geographic data, while leaving accuracy and local interpretation as real concerns rather than assuming crowdsourcing makes every mapped feature reliable.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'OpenStreetMap · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/OpenStreetMap',
          note: 'Assigned external article; source-derived explanations and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
    },
    questions: [
      {
        id: 'b5-openstreetmap-1',
        prompt:
          'Why can contributions to OpenStreetMap support both navigation software and printed disaster-response maps?',
        options: [
          {
            id: '0',
            text: 'Every application displays the same live screenshot.',
          },
          {
            id: '1',
            text: 'Contributions consist only of satellite photographs.',
          },
          {
            id: '2',
            text: 'The project supplies structured geographic data reusable by different tools.',
          },
          {
            id: '3',
            text: 'Printed maps are made independently of the database.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Introduction and Humanitarian aid describe reusable data serving navigation and printed maps.',
        sectionIds: ['database'],
        evidence:
          'Introduction and Humanitarian aid describe reusable data serving navigation and printed maps. Verified in assigned source section: Introduction.',
        sourceSection: {
          title: 'Wikipedia: Introduction',
          url: 'https://en.wikipedia.org/wiki/OpenStreetMap',
        },
      },
      {
        id: 'b5-openstreetmap-2',
        prompt:
          'Two streets meet at a junction. Which representation explicitly records their connection in the described data model?',
        options: [
          {
            id: '0',
            text: 'A node shared by both ways',
          },
          {
            id: '1',
            text: 'Two ways with similar names',
          },
          {
            id: '2',
            text: 'Two separate nodes with unrelated tags',
          },
          {
            id: '3',
            text: 'A relation containing only one street name',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Data structure states that multiple ways can share a node to represent a street intersection.',
        sectionIds: ['topology'],
        evidence:
          'Data structure states that multiple ways can share a node to represent a street intersection. Verified in assigned source section: Data_structure.',
        sourceSection: {
          title: 'Wikipedia: Data structure',
          url: 'https://en.wikipedia.org/wiki/OpenStreetMap#Data_structure',
        },
      },
      {
        id: 'b5-openstreetmap-3',
        prompt: 'Which description of tagging matches the article?',
        options: [
          {
            id: '0',
            text: 'Tags must all be approved by the foundation before use.',
          },
          {
            id: '1',
            text: 'Tags replace geographic coordinates entirely.',
          },
          {
            id: '2',
            text: 'Every tag automatically has the same meaning in all applications.',
          },
          {
            id: '3',
            text: 'Editors may use new tags while community conventions recommend shared meanings.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Any tags you like describes recommended ontology and optional proposals rather than mandatory approval.',
        sectionIds: ['tags'],
        evidence:
          'Any tags you like describes recommended ontology and optional proposals rather than mandatory approval. Verified in assigned source section: "Any_tags_you_like".',
        sourceSection: {
          title: 'Wikipedia: "Any tags you like"',
          url: 'https://en.wikipedia.org/wiki/OpenStreetMap#"Any_tags_you_like"',
        },
      },
      {
        id: 'b5-openstreetmap-4',
        prompt:
          'A wrong label has been corrected in OSM but remains in another app. Which mechanism described in the article could explain that?',
        options: [
          {
            id: '0',
            text: 'Corrections are prohibited after a feature is first published.',
          },
          {
            id: '1',
            text: 'The app uses a copy that has not yet incorporated the correction.',
          },
          {
            id: '2',
            text: 'All edits await approval before entering the source database.',
          },
          {
            id: '3',
            text: 'OSM stores no revision history.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Quality assurance explicitly describes downstream replication lag after source correction.',
        sectionIds: ['review'],
        evidence:
          'Quality assurance explicitly describes downstream replication lag after source correction. Verified in assigned source section: Quality_assurance.',
        sourceSection: {
          title: 'Wikipedia: Quality assurance',
          url: 'https://en.wikipedia.org/wiki/OpenStreetMap#Quality_assurance',
        },
      },
      {
        id: 'b5-openstreetmap-5',
        prompt: 'What did the Haiti response demonstrate about the mapping workflow?',
        options: [
          {
            id: '0',
            text: 'Only surveyors physically present could add useful data.',
          },
          {
            id: '1',
            text: 'Mapping eliminated the need for aid organizations to operate locally.',
          },
          {
            id: '2',
            text: 'Remote imagery-based contributions could produce data used by field responders.',
          },
          {
            id: '3',
            text: 'Volunteer maps could only be used as online images.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Humanitarian aid describes satellite-based volunteer mapping followed by use by relief organizations.',
        sectionIds: ['relief'],
        evidence:
          'Humanitarian aid describes satellite-based volunteer mapping followed by use by relief organizations. Verified in assigned source section: Humanitarian_aid.',
        sourceSection: {
          title: 'Wikipedia: Humanitarian aid',
          url: 'https://en.wikipedia.org/wiki/OpenStreetMap#Humanitarian_aid',
        },
      },
    ],
  },
  {
    id: 'reading-kowloon',
    category: 'reading',
    title: 'Kowloon Walled City',
    topic: 'Kowloon Walled City',
    difficulty: 'Challenging',
    article: {
      title: 'Kowloon Walled City',
      url: 'https://en.wikipedia.org/wiki/Kowloon_Walled_City',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-28',
      preview:
        'A small jurisdictional enclave became an extraordinarily dense neighborhood of interconnected buildings. The assigned opening sections examine its political history, everyday life, physical layout and eventual demolition.',
      scope: {
        wordCount: 2529,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Kowloon_Walled_City',
            wordCount: 426,
            anchors: [''],
          },
          {
            title: 'History',
            url: 'https://en.wikipedia.org/wiki/Kowloon_Walled_City#History',
            wordCount: 1525,
            anchors: ['History', 'Military_outpost', 'Urban_settlement', 'Eviction_and_demolition'],
          },
          {
            title: 'The city before demolition',
            url: 'https://en.wikipedia.org/wiki/Kowloon_Walled_City#The_city_before_demolition',
            wordCount: 578,
            anchors: [
              'The_city_before_demolition',
              'Layout_and_architecture',
              'Demography',
              'Culture',
            ],
          },
        ],
      },
    },
    reading: {
      title: 'Kowloon Walled City — explanations',
      authorship:
        'Original questions and adapted factual explanations with source attribution; CC BY-SA 4.0.',
      sections: [
        {
          id: 'jurisdiction',
          title: 'A disputed jurisdiction shaped development',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The agreement extending British control over the New Territories excluded the Walled City. Later political changes and conflicting claims complicated who would exercise authority there. The article connects that history with periods of limited enforcement and the growth of unregulated settlement.',
            },
            {
              kind: 'paragraph',
              text: 'This is more specific than saying the site had never belonged to any government. It originated as a military fort, and claims to jurisdiction continued. The important distinction is between a formal claim and the practical exercise of administration. Describing the whole history as uniformly lawless would also miss later policing and public services.',
            },
          ],
        },
        {
          id: 'height',
          title: 'A constraint can come from outside the neighborhood',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Dense construction encouraged vertical expansion, but nearby Kai Tak Airport constrained building height through its flight path. The article describes a ceiling around thirteen or fourteen stories. Limited ground area and a limit on height together shaped the resulting built form.',
            },
            {
              kind: 'paragraph',
              text: 'This does not mean the structures were uniformly well regulated or carefully planned. One external operational constraint can matter even where many ordinary building controls are weak. The question asks for the particular height constraint supplied in the source, not an assumption that all regulation was either present or absent.',
            },
          ],
        },
        {
          id: 'paths',
          title: 'Circulation extended above street level',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Passageways, stairs and connections between buildings formed an informal network at upper levels. The article reports that a person could cross the settlement without returning to ground level. Understanding that network changes the picture from isolated towers on separate plots to an interconnected inhabited structure.',
            },
            {
              kind: 'paragraph',
              text: 'The narrow, poorly lit ground-level alleys were still part of everyday circulation. The upper connections supplemented that environment rather than turning every floor into a planned public boulevard. The account illustrates how people adapted movement to tightly packed construction, without implying that the resulting routes met modern accessibility or safety standards.',
            },
          ],
        },
        {
          id: 'community',
          title: 'Crime did not describe every resident',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Triad influence and criminal activity are substantial parts of the history, but the article also describes peaceful residents, small businesses, welfare groups and community organizations. Police raids later reduced organized crime’s control. A useful summary must preserve both the difficulties and the ordinary social life.',
            },
            {
              kind: 'paragraph',
              text: 'The contrast is not an invitation to romanticize poor sanitation or unsafe construction. It prevents inferring that every resident participated in crime from the presence of powerful criminal groups. Rooftops, the yamen and local associations provided gathering places and support under difficult conditions.',
            },
          ],
        },
        {
          id: 'end',
          title: 'Demolition involved negotiation and displacement',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The eventual demolition followed an agreement between the British and Chinese governments, an announced clearance plan, compensation arrangements and the removal of residents and businesses. The article reports resistance and forced evictions as well as compensation. The outcome was a park with some historic structures retained.',
            },
            {
              kind: 'paragraph',
              text: 'A cleared site is therefore not evidence that the neighborhood had simply emptied through voluntary abandonment. Political decisions and the process of relocating people are central to the transition. The quiz avoids relying on an exact population figure because the article discusses the uncertainty and inconsistent boundaries behind some estimates.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'Kowloon Walled City · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/Kowloon_Walled_City',
          note: 'Assigned external article; source-derived explanations and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
    },
    questions: [
      {
        id: 'b5-kowloon-1',
        prompt:
          'Which earlier arrangement helps explain the Walled City’s unusual jurisdictional position?',
        options: [
          {
            id: '0',
            text: 'It was created as a wholly private development owned by the airport.',
          },
          {
            id: '1',
            text: 'It became a new sovereign state after the Second World War.',
          },
          {
            id: '2',
            text: 'It had no prior military or administrative role.',
          },
          {
            id: '3',
            text: 'It was excluded from the agreement leasing the New Territories to Britain.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Military outpost explains the exclusion in the 1898 convention and subsequent jurisdictional claims.',
        sectionIds: ['jurisdiction'],
        evidence:
          'Military outpost explains the exclusion in the 1898 convention and subsequent jurisdictional claims. Verified in assigned source section: Military_outpost.',
        sourceSection: {
          title: 'Wikipedia: Military outpost',
          url: 'https://en.wikipedia.org/wiki/Kowloon_Walled_City#Military_outpost',
        },
      },
      {
        id: 'b5-kowloon-2',
        prompt:
          'What external factor limited the height of buildings despite pressure for dense construction?',
        options: [
          {
            id: '0',
            text: 'A requirement that every roof remain below the old defensive wall',
          },
          {
            id: '1',
            text: 'The flight path of aircraft using Kai Tak Airport',
          },
          {
            id: '2',
            text: 'A ban on buildings above the yamen’s two stories',
          },
          {
            id: '3',
            text: 'The need to preserve a direct view of the harbor from every home',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Urban settlement and Layout and architecture connect the height limit to Kai Tak Airport.',
        sectionIds: ['height'],
        evidence:
          'Urban settlement and Layout and architecture connect the height limit to Kai Tak Airport. Verified in assigned source section: Layout_and_architecture.',
        sourceSection: {
          title: 'Wikipedia: Layout and architecture',
          url: 'https://en.wikipedia.org/wiki/Kowloon_Walled_City#Layout_and_architecture',
        },
      },
      {
        id: 'b5-kowloon-3',
        prompt: 'What distinguished circulation through the settlement’s buildings?',
        options: [
          {
            id: '0',
            text: 'All movement required returning to the ground after each building.',
          },
          {
            id: '1',
            text: 'A single planned subway connected every apartment.',
          },
          {
            id: '2',
            text: 'Upper-level passages and stairs allowed routes across the settlement.',
          },
          {
            id: '3',
            text: 'Roof access was reserved exclusively for police patrols.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Layout and architecture describes a connected upper-level network spanning the city.',
        sectionIds: ['paths'],
        evidence:
          'Layout and architecture describes a connected upper-level network spanning the city. Verified in assigned source section: Layout_and_architecture.',
        sourceSection: {
          title: 'Wikipedia: Layout and architecture',
          url: 'https://en.wikipedia.org/wiki/Kowloon_Walled_City#Layout_and_architecture',
        },
      },
      {
        id: 'b5-kowloon-4',
        prompt:
          'Which summary best preserves the article’s distinction between criminal influence and residents’ lives?',
        options: [
          {
            id: '0',
            text: 'Criminal groups were influential, but many residents lived peacefully and organized community life.',
          },
          {
            id: '1',
            text: 'Organized crime made peaceful commerce impossible throughout its history.',
          },
          {
            id: '2',
            text: 'The crime reputation was entirely invented after demolition.',
          },
          {
            id: '3',
            text: 'Government services and community organizations never operated there.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Urban settlement and Culture distinguish triad power from peaceful lives, businesses and associations.',
        sectionIds: ['community'],
        evidence:
          'Urban settlement and Culture distinguish triad power from peaceful lives, businesses and associations. Verified in assigned source section: Urban_settlement.',
        sourceSection: {
          title: 'Wikipedia: Urban settlement',
          url: 'https://en.wikipedia.org/wiki/Kowloon_Walled_City#Urban_settlement',
        },
      },
      {
        id: 'b5-kowloon-5',
        prompt: 'Which sequence matches the transition described in the article?',
        options: [
          {
            id: '0',
            text: 'Voluntary abandonment, airport expansion, then a new military fort',
          },
          {
            id: '1',
            text: 'Joint clearance decision, compensation and eviction, demolition, then a park',
          },
          {
            id: '2',
            text: 'An earthquake destroyed the settlement, followed by a replacement city',
          },
          {
            id: '3',
            text: 'Demolition preceded all negotiations about residents and compensation',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Eviction and demolition gives the negotiated clearance, compensation and eviction process before the park.',
        sectionIds: ['end'],
        evidence:
          'Eviction and demolition gives the negotiated clearance, compensation and eviction process before the park. Verified in assigned source section: Eviction_and_demolition.',
        sourceSection: {
          title: 'Wikipedia: Eviction and demolition',
          url: 'https://en.wikipedia.org/wiki/Kowloon_Walled_City#Eviction_and_demolition',
        },
      },
    ],
  },
  {
    id: 'reading-dunbar',
    category: 'reading',
    title: "Dunbar's number",
    topic: 'Dunbar%27s number',
    difficulty: 'Challenging',
    article: {
      title: "Dunbar's number",
      url: 'https://en.wikipedia.org/wiki/Dunbar%27s_number',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-28',
      preview:
        'A widely repeated number about friendship began as an extrapolation from primate research. Read the proposal alongside its assumptions, applications and statistical criticisms.',
      scope: {
        wordCount: 1588,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Dunbar%27s_number',
            wordCount: 251,
            anchors: [''],
          },
          {
            title: 'Research background',
            url: 'https://en.wikipedia.org/wiki/Dunbar%27s_number#Research_background',
            wordCount: 572,
            anchors: ['Research_background'],
          },
          {
            title: 'Applications',
            url: 'https://en.wikipedia.org/wiki/Dunbar%27s_number#Applications',
            wordCount: 174,
            anchors: ['Applications'],
          },
          {
            title: 'Alternative numbers',
            url: 'https://en.wikipedia.org/wiki/Dunbar%27s_number#Alternative_numbers',
            wordCount: 114,
            anchors: ['Alternative_numbers'],
          },
          {
            title: 'Criticism',
            url: 'https://en.wikipedia.org/wiki/Dunbar%27s_number#Criticism',
            wordCount: 477,
            anchors: ['Criticism'],
          },
        ],
      },
    },
    reading: {
      title: "Dunbar's number — explanations",
      authorship:
        'Original questions and adapted factual explanations with source attribution; CC BY-SA 4.0.',
      sections: [
        {
          id: 'claim',
          title: 'Define the relationship being counted',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Dunbar’s proposal concerns stable social relationships, not every face a person recognizes or every contact stored in a phone. The article describes knowing who people are and how they relate to others. A follower count and a maintained social network can therefore measure different things even if both are expressed as numbers of people.',
            },
            {
              kind: 'paragraph',
              text: 'The commonly repeated value is an estimate associated with a hypothesis, not a rule that a person becomes incapable of one more acquaintance at a precise threshold. The article itself reports ranges, context and disagreement. The quiz asks what the proposal means, without treating the proposal as settled neuroscience.',
            },
          ],
        },
        {
          id: 'inference',
          title: 'An extrapolation carries its assumptions',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The original analysis used an observed relationship between relative brain size and social group size in nonhuman primates, then extrapolated to humans. Extrapolation means using a relationship beyond the observations that supplied it. It can produce a testable prediction, but relies on the relationship remaining relevant in the new case.',
            },
            {
              kind: 'paragraph',
              text: 'That origin differs from directly counting every human friendship and discovering a universal cutoff. Differences between species, ecological conditions and definitions of groups can affect the inference. The article’s research background describes the result as exploratory, with substantial uncertainty even in the initial estimate.',
            },
          ],
        },
        {
          id: 'maintenance',
          title: 'Capacity is not the only condition',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The assigned account connects cohesive groups with time spent maintaining social relationships, opportunities for contact and incentives to remain together. A proposed processing limit does not automatically make every dispersed collection of people a functioning community of that size.',
            },
            {
              kind: 'paragraph',
              text: 'Language is presented as a possible less costly way of maintaining social bonds than one-to-one physical grooming. This is an explanatory hypothesis in the article, not an experimentally demonstrated origin story. The useful distinction is between knowing that a relationship exists and doing the social work needed to sustain it.',
            },
          ],
        },
        {
          id: 'uncertainty',
          title: 'A precise-looking point estimate can hide a wide range',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The criticism section describes reanalyses yielding markedly different central estimates and very broad uncertainty intervals. The issue is not merely whether the preferred number should be adjusted slightly. If reasonable methods support very different values, the evidence may not justify a single useful numerical limit from that approach.',
            },
            {
              kind: 'paragraph',
              text: 'This does not establish that humans have unlimited attention or that maintaining relationships has no cost. It challenges the strength of a specific numerical inference. Separating a broad idea from one attempted measurement prevents both uncritical acceptance and an equally sweeping rejection.',
            },
          ],
        },
        {
          id: 'application',
          title: 'Use in organizations is not proof of a biological law',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The article reports that organizations and software designers have found the idea useful enough to apply. Adopting a number in a management scheme shows influence and a design choice; it does not by itself validate the original causal explanation or prove that every organization should use the same size.',
            },
            {
              kind: 'paragraph',
              text: 'A design could work for several reasons, including local management practices, communication patterns or the tasks being performed. To evaluate a biological hypothesis, evidence would need to discriminate among such explanations. The reading’s applications and criticism should therefore be considered together rather than treating popularity as independent confirmation.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'Dunbar%27s number · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/Dunbar%27s_number',
          note: 'Assigned external article; source-derived explanations and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
    },
    questions: [
      {
        id: 'b5-dunbar-1',
        prompt: 'What does the proposed limit chiefly concern?',
        options: [
          {
            id: '0',
            text: 'Stable social relationships a person can maintain',
          },
          {
            id: '1',
            text: 'All names a person can recognize',
          },
          {
            id: '2',
            text: 'Every contact saved on a device',
          },
          {
            id: '3',
            text: 'The largest crowd a person can physically join',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Introduction defines stable social relationships rather than recognition or contact lists.',
        sectionIds: ['claim'],
        evidence:
          'Introduction defines stable social relationships rather than recognition or contact lists. Verified in assigned source section: Introduction.',
        sourceSection: {
          title: 'Wikipedia: Introduction',
          url: 'https://en.wikipedia.org/wiki/Dunbar%27s_number',
        },
      },
      {
        id: 'b5-dunbar-2',
        prompt: 'How was the original human estimate obtained?',
        options: [
          {
            id: '0',
            text: 'A census established an identical cutoff in every society.',
          },
          {
            id: '1',
            text: 'A randomized trial assigned humans different brain sizes.',
          },
          {
            id: '2',
            text: 'A relationship in nonhuman primates was extrapolated to humans.',
          },
          {
            id: '3',
            text: 'Organizations voted on their preferred team size.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'Research background describes primate regression and extrapolation.',
        sectionIds: ['inference'],
        evidence:
          'Research background describes primate regression and extrapolation. Verified in assigned source section: Research_background.',
        sourceSection: {
          title: 'Wikipedia: Research background',
          url: 'https://en.wikipedia.org/wiki/Dunbar%27s_number#Research_background',
        },
      },
      {
        id: 'b5-dunbar-3',
        prompt:
          'Which qualification does the research background place on maintaining a cohesive group near the proposed size?',
        options: [
          {
            id: '0',
            text: 'It requires no time once the members have met.',
          },
          {
            id: '1',
            text: 'Contact, relationship-maintenance time and incentives to remain together matter.',
          },
          {
            id: '2',
            text: 'Physical dispersal necessarily strengthens familiarity.',
          },
          {
            id: '3',
            text: 'It depends only on storing everyone’s name.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Research background discusses social grooming time, proximity and incentives.',
        sectionIds: ['maintenance'],
        evidence:
          'Research background discusses social grooming time, proximity and incentives. Verified in assigned source section: Research_background.',
        sourceSection: {
          title: 'Wikipedia: Research background',
          url: 'https://en.wikipedia.org/wiki/Dunbar%27s_number#Research_background',
        },
      },
      {
        id: 'b5-dunbar-4',
        prompt:
          'Why do very wide uncertainty intervals in reanalyses weaken claims about one precise number?',
        options: [
          {
            id: '0',
            text: 'They prove every person has exactly the same network size.',
          },
          {
            id: '1',
            text: 'They show that estimates are independent of model assumptions.',
          },
          {
            id: '2',
            text: 'They establish that relationships have no cognitive or time costs.',
          },
          {
            id: '3',
            text: 'They leave a broad range of plausible values under the methods used.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Criticism reports broad intervals and method-sensitive estimates, questioning derivation of one number.',
        sectionIds: ['uncertainty'],
        evidence:
          'Criticism reports broad intervals and method-sensitive estimates, questioning derivation of one number. Verified in assigned source section: Criticism.',
        sourceSection: {
          title: 'Wikipedia: Criticism',
          url: 'https://en.wikipedia.org/wiki/Dunbar%27s_number#Criticism',
        },
      },
      {
        id: 'b5-dunbar-5',
        prompt:
          'An organization adopts a 150-person grouping after reading about the idea. What does that establish by itself?',
        options: [
          {
            id: '0',
            text: 'A universal biological limit has been independently proved.',
          },
          {
            id: '1',
            text: 'All smaller groups must be less cohesive.',
          },
          {
            id: '2',
            text: 'The hypothesis influenced an organizational design choice.',
          },
          {
            id: '3',
            text: 'The primate extrapolation has no uncertainty.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Applications reports adoption; it does not establish causal or universal validation. This is an explicit distinction between use and proof.',
        sectionIds: ['application'],
        evidence:
          'Applications reports adoption; it does not establish causal or universal validation. This is an explicit distinction between use and proof. Verified in assigned source section: Applications.',
        sourceSection: {
          title: 'Wikipedia: Applications',
          url: 'https://en.wikipedia.org/wiki/Dunbar%27s_number#Applications',
        },
      },
    ],
  },
  {
    id: 'reading-pneumatic',
    category: 'reading',
    title: 'Pneumatic tube',
    topic: 'Pneumatic tube',
    difficulty: 'Challenging',
    article: {
      title: 'Pneumatic tube',
      url: 'https://en.wikipedia.org/wiki/Pneumatic_tube',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-28',
      preview:
        'Before instant digital messages, cities sent urgent objects through air-driven networks. Some uses disappeared while others persisted because the payload itself still needed to move.',
      scope: {
        wordCount: 1232,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Pneumatic_tube',
            wordCount: 176,
            anchors: [''],
          },
          {
            title: 'Historical use',
            url: 'https://en.wikipedia.org/wiki/Pneumatic_tube#Historical_use',
            wordCount: 333,
            anchors: ['Historical_use'],
          },
          {
            title: 'In postal service',
            url: 'https://en.wikipedia.org/wiki/Pneumatic_tube#In_postal_service',
            wordCount: 513,
            anchors: ['In_postal_service'],
          },
          {
            title: 'Technical characteristics',
            url: 'https://en.wikipedia.org/wiki/Pneumatic_tube#Technical_characteristics',
            wordCount: 210,
            anchors: ['Technical_characteristics'],
          },
        ],
      },
    },
    reading: {
      title: 'Pneumatic tube — explanations',
      authorship:
        'Original questions and adapted factual explanations with source attribution; CC BY-SA 4.0.',
      sections: [
        {
          id: 'payload',
          title: 'The air moves a carrier containing an object',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Pneumatic tube systems use compressed air or partial vacuum to propel carriers through tubes. Their characteristic payload is a solid object inside a container, unlike an ordinary pipeline whose transported product is the flowing fluid itself. The distinction concerns what needs to arrive at the destination.',
            },
            {
              kind: 'paragraph',
              text: 'That is why digitizing a message can replace one use but not every use. An electronic copy of a letter may convey its information, whereas a physical specimen or object still has to travel. The article’s historical and modern examples illustrate a change in the valuable payloads rather than simply a technology becoming universally obsolete.',
            },
          ],
        },
        {
          id: 'complement',
          title: 'One communication technology can connect to another',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The stock-exchange installation carried financial information to telegraph offices, where it could be transmitted onward over telegraph wires. The tube and the telegraph solved different links of the journey. A fast long-distance network still benefits from faster delivery between a local source and its station.',
            },
            {
              kind: 'paragraph',
              text: 'The article contrasts that local link with runners or placing trained telegraph operators at the exchange. The case is not one technology cleanly replacing every part of another. It shows infrastructure being combined into a workflow, with a bottleneck in short-distance collection affecting the usefulness of rapid onward transmission.',
            },
          ],
        },
        {
          id: 'replacement',
          title: 'Distinguish information from a physical original',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The Paris pneumatic-post network eventually gave way to computers and fax machines. For many document deliveries, transmitting the information electronically could substitute for carrying paper through a tube. Other systems persisted where transporting the object remained useful.',
            },
            {
              kind: 'paragraph',
              text: 'This explanation does not assert that every tube network closed at the same date or for the same reason. The article gives different city histories, including damage from flooding in Prague. The question focuses on the change in what a recipient needed: the informational contents of many messages could arrive without moving the original carrier.',
            },
          ],
        },
        {
          id: 'braking',
          title: 'Arrival is part of transport performance',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A transport system must not only move a carrier quickly but bring it to rest appropriately. The technical section describes changing air pressure to slow capsules and reduce the jarring arrivals associated with earlier systems. Controlling deceleration can make a delivery more suitable for fragile contents.',
            },
            {
              kind: 'paragraph',
              text: 'Speed alone therefore does not determine whether a tube system is useful for a particular payload. A fast trip ending in a damaging impact would fail the task. The article describes improvements to the motion profile; it does not imply every delicate object is automatically safe in every pneumatic system.',
            },
          ],
        },
        {
          id: 'tracking',
          title: 'A carrier network can also be an information network',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Modern systems can use computer control and identifiers such as RFID to track carriers, destinations and transport information. Those data support priority handling and analysis of internal turnaround times. The physical tube moves the object while the information layer helps manage its journey.',
            },
            {
              kind: 'paragraph',
              text: 'Tracking and propulsion are distinct functions. An RFID identifier does not supply the air pressure driving a capsule, and knowing a destination does not itself move the payload. The system combines mechanics with routing and monitoring, much as the earlier telegraph example combined local transport with long-distance communication.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'Pneumatic tube · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/Pneumatic_tube',
          note: 'Assigned external article; source-derived explanations and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
    },
    questions: [
      {
        id: 'b5-pneumatic-1',
        prompt:
          'What distinguishes the characteristic payload of a pneumatic tube system from that of an ordinary fluid pipeline?',
        options: [
          {
            id: '0',
            text: 'The payload is exclusively the compressed air itself.',
          },
          {
            id: '1',
            text: 'The transported item is a solid object in a carrier.',
          },
          {
            id: '2',
            text: 'The item moves only as an electronic signal.',
          },
          {
            id: '3',
            text: 'The pipeline must contain no air.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Introduction contrasts cylindrical carriers for solid objects with fluid pipelines.',
        sectionIds: ['payload'],
        evidence:
          'Introduction contrasts cylindrical carriers for solid objects with fluid pipelines. Verified in assigned source section: Introduction.',
        sourceSection: {
          title: 'Wikipedia: Introduction',
          url: 'https://en.wikipedia.org/wiki/Pneumatic_tube',
        },
      },
      {
        id: 'b5-pneumatic-2',
        prompt: 'How did the London stock-exchange tube complement the telegraph service?',
        options: [
          {
            id: '0',
            text: 'It carried information locally to offices for onward telegraphic transmission.',
          },
          {
            id: '1',
            text: 'It replaced all long-distance telegraph wires.',
          },
          {
            id: '2',
            text: 'It converted stock prices automatically into coins.',
          },
          {
            id: '3',
            text: 'It eliminated the need to collect information at the exchange.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Historical use describes the local connection to the Electric Telegraph Company.',
        sectionIds: ['complement'],
        evidence:
          'Historical use describes the local connection to the Electric Telegraph Company. Verified in assigned source section: Historical_use.',
        sourceSection: {
          title: 'Wikipedia: Historical use',
          url: 'https://en.wikipedia.org/wiki/Pneumatic_tube#Historical_use',
        },
      },
      {
        id: 'b5-pneumatic-3',
        prompt:
          'Why could computers and fax machines replace much pneumatic-post work while some physical-delivery uses remained?',
        options: [
          {
            id: '0',
            text: 'Electronic communication makes all physical objects unnecessary.',
          },
          {
            id: '1',
            text: 'Tube propulsion depends on handwritten messages.',
          },
          {
            id: '2',
            text: 'Information can be transmitted without moving paper, but some payloads must still move physically.',
          },
          {
            id: '3',
            text: 'Digitization only affects transport over intercontinental distances.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Introduction and In postal service contrast digital replacement with continuing physical-object uses.',
        sectionIds: ['replacement'],
        evidence:
          'Introduction and In postal service contrast digital replacement with continuing physical-object uses. Verified in assigned source section: In_postal_service.',
        sourceSection: {
          title: 'Wikipedia: In postal service',
          url: 'https://en.wikipedia.org/wiki/Pneumatic_tube#In_postal_service',
        },
      },
      {
        id: 'b5-pneumatic-4',
        prompt: 'What benefit does controlled variation in air pressure provide near arrival?',
        options: [
          {
            id: '0',
            text: 'It changes the address printed on the carrier.',
          },
          {
            id: '1',
            text: 'It replaces every need for a protective carrier.',
          },
          {
            id: '2',
            text: 'It guarantees a higher maximum speed at all times.',
          },
          {
            id: '3',
            text: 'It allows gentler braking and less jarring delivery.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Technical characteristics describes gradual braking to reduce jarring arrivals.',
        sectionIds: ['braking'],
        evidence:
          'Technical characteristics describes gradual braking to reduce jarring arrivals. Verified in assigned source section: Technical_characteristics.',
        sourceSection: {
          title: 'Wikipedia: Technical characteristics',
          url: 'https://en.wikipedia.org/wiki/Pneumatic_tube#Technical_characteristics',
        },
      },
      {
        id: 'b5-pneumatic-5',
        prompt: 'What role do RFID and computer control play in the modern systems described?',
        options: [
          {
            id: '0',
            text: 'Tracking carriers and helping manage destinations, priorities and flow',
          },
          {
            id: '1',
            text: 'Generating all of the pressure needed to propel capsules',
          },
          {
            id: '2',
            text: 'Making physical tubes unnecessary between stations',
          },
          {
            id: '3',
            text: 'Preventing any need to consider transport time',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Technical characteristics describes carrier data and computer-controlled tracking and priority deliveries.',
        sectionIds: ['tracking'],
        evidence:
          'Technical characteristics describes carrier data and computer-controlled tracking and priority deliveries. Verified in assigned source section: Technical_characteristics.',
        sourceSection: {
          title: 'Wikipedia: Technical characteristics',
          url: 'https://en.wikipedia.org/wiki/Pneumatic_tube#Technical_characteristics',
        },
      },
    ],
  },
];
